import { Injectable, inject } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { StockHoldTimeoutService as StockHoldTimeoutApiService } from '@app/api/stock-service/api/stockHoldTimeout.service';
import { StockHoldTimeoutConfigDTO } from '@app/api/stock-service/model/stockHoldTimeoutConfigDTO';
import { StockHoldTimeoutConfigPageRequestDTO } from '@app/api/stock-service/model/stockHoldTimeoutConfigPageRequestDTO';
import { StockHoldTimeoutConfigVO } from '@app/api/stock-service/model/stockHoldTimeoutConfigVO';
import { PageInfoStockHoldTimeoutConfigVO } from '@app/api/stock-service/model/pageInfoStockHoldTimeoutConfigVO';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { StockHoldTimeoutConfigItem } from '../stock-hold-timeout.component';

const JSON_ACCEPT_OPTIONS = { httpHeaderAccept: 'application/json' } as const;

@Injectable({
  providedIn: 'root',
})
export class StockHoldTimeoutFeatureService {
  private readonly api = inject(StockHoldTimeoutApiService);

  pageConfigs(
    page: number,
    size: number,
    holdCategory?: StockHoldTimeoutConfigPageRequestDTO.HoldCategoryEnum,
    enabled?: boolean,
  ): Observable<{ configs: StockHoldTimeoutConfigItem[]; totalRecords: number }> {
    const request: StockHoldTimeoutConfigPageRequestDTO = {
      page,
      size,
      sortBy: 'holdCategory',
      ascending: true,
      holdCategory,
      enabled,
    };

    return this.api
      .pageConfigs(request, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: PageInfoStockHoldTimeoutConfigVO | Blob) =>
          this.handleBlobResponse<PageInfoStockHoldTimeoutConfigVO>(response),
        ),
        map((response) => ({
          configs: (response.pageContent || []).map((vo) => this.mapVo(vo)),
          totalRecords: response.totalElements || 0,
        })),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  createConfig(
    config: StockHoldTimeoutConfigItem,
  ): Observable<StockHoldTimeoutConfigItem> {
    const dto = this.mapToDto(config);
    delete dto.id;
    return this.api
      .createConfig(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: StockHoldTimeoutConfigVO | Blob) =>
          this.handleBlobResponse<StockHoldTimeoutConfigVO>(response),
        ),
        map((vo) => this.mapVo(vo)),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  updateConfig(
    config: StockHoldTimeoutConfigItem,
  ): Observable<StockHoldTimeoutConfigItem> {
    const dto = this.mapToDto(config);
    return this.api
      .updateConfig(dto, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        switchMap((response: StockHoldTimeoutConfigVO | Blob) =>
          this.handleBlobResponse<StockHoldTimeoutConfigVO>(response),
        ),
        map((vo) => this.mapVo(vo)),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  deleteConfig(id: number): Observable<void> {
    return this.api
      .deleteConfig(id, 'body', false, JSON_ACCEPT_OPTIONS as any)
      .pipe(
        map(() => undefined),
        catchError((error) => this.handleBlobError(error)),
      );
  }

  private handleBlobResponse<T>(response: Blob | T): Observable<T> {
    if (response instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(response)) as Observable<T>;
    }
    return of(response as T);
  }

  private handleBlobError(error: unknown): Observable<never> {
    const err = error as { error?: unknown };
    if (err?.error instanceof Blob) {
      return from(BlobConverterUtil.convertToJson(err.error)).pipe(
        catchError(() => throwError(() => error)),
        switchMap((parsedError: unknown) =>
          throwError(() => ({ ...(error as object), error: parsedError })),
        ),
      );
    }
    return throwError(() => error);
  }

  private mapVo(vo: StockHoldTimeoutConfigVO): StockHoldTimeoutConfigItem {
    return {
      id: vo.id,
      holdCategory: vo.holdCategory || '',
      timeoutMinutes: vo.timeoutMinutes ?? 0,
      enabled: vo.enabled ?? true,
    };
  }

  private mapToDto(
    config: StockHoldTimeoutConfigItem,
  ): StockHoldTimeoutConfigDTO {
    return {
      id: config.id,
      holdCategory:
        config.holdCategory as StockHoldTimeoutConfigDTO.HoldCategoryEnum,
      timeoutMinutes: config.timeoutMinutes,
      enabled: config.enabled,
    };
  }
}
